import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  noIndex?: boolean;
  ogTitle?: string;
  ogDescription?: string;
  schema?: object | object[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  noIndex = false,
  ogTitle,
  ogDescription,
  schema,
}: SEOProps) {
  useEffect(() => {
    // Title
    document.title = title;

    // Helper to set/create a <meta> tag
    const setMeta = (selector: string, value: string, attr: string = 'content') => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        const attrName = selector.includes('[property=') ? 'property' : 'name';
        const attrValue = selector.match(/\[(?:name|property)="([^"]+)"\]/)?.[1] ?? '';
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    // Helper to set/create a <link> tag
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.rel = rel;
        document.head.appendChild(el);
      }
      el.href = href;
    };

    // Primary meta
    setMeta('meta[name="description"]', description);
    if (keywords) setMeta('meta[name="keywords"]', keywords);
    setMeta('meta[name="robots"]', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Last-modified
    setMeta('meta[name="last-modified"]', new Date().toISOString().split('T')[0]);

    // Open Graph
    setMeta('meta[property="og:title"]', ogTitle ?? title);
    setMeta('meta[property="og:description"]', ogDescription ?? description);
    setMeta('meta[property="og:type"]', 'website');
    const canonicalHref = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
    setMeta('meta[property="og:url"]', canonicalHref);

    // Twitter
    setMeta('meta[property="twitter:title"]', ogTitle ?? title);
    setMeta('meta[property="twitter:description"]', ogDescription ?? description);
    setMeta('meta[property="twitter:card"]', 'summary_large_image');
    setMeta('meta[property="twitter:url"]', canonicalHref);

    // Canonical link
    setLink('canonical', canonicalHref);

    // Schema.org JSON-LD
    const SCHEMA_ID = 'page-jsonld';
    let scriptEl = document.getElementById(SCHEMA_ID) as HTMLScriptElement | null;
    if (schema) {
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.type = 'application/ld+json';
        scriptEl.id = SCHEMA_ID;
        document.head.appendChild(scriptEl);
      }
      const schemas = Array.isArray(schema) ? schema : [schema];
      scriptEl.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
    } else if (scriptEl) {
      scriptEl.remove();
    }

    // cleanup on unmount: remove page-level schema so next page starts clean
    return () => {
      const el = document.getElementById(SCHEMA_ID);
      if (el) el.remove();
    };
  }, [title, description, keywords, canonical, noIndex, ogTitle, ogDescription, schema]);

  return null;
}